import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Home, BookOpen, Mail } from "lucide-react";

/* ============================================================
   404 — A cinematic "lost the plot" page
   ============================================================ */
export default function NotFound() {
  const [glitch, setGlitch] = useState(false);
  const [typed, setTyped] = useState("");
  const fullPath =
    typeof window !== "undefined" ? window.location.pathname : "/unknown";

  useEffect(() => {
    document.title = "404 — Plot twist · Rishikesh N";
    // Random glitch on the 404 number
    const g = setInterval(() => {
      setGlitch(true);
      setTimeout(() => setGlitch(false), 180);
    }, 3200);
    // Typewriter effect on the path
    let i = 0;
    const t = setInterval(() => {
      if (i <= fullPath.length) {
        setTyped(fullPath.slice(0, i));
        i++;
      } else {
        clearInterval(t);
      }
    }, 60);
    return () => {
      clearInterval(g);
      clearInterval(t);
    };
  }, [fullPath]);

  return (
    <div className="min-h-screen bg-[var(--bg)] text-white relative overflow-hidden flex items-center">
      <style>{`
        @keyframes nf-glow {
          0%, 100% { transform: translate(0,0) scale(1); opacity: 0.5; }
          50% { transform: translate(40px,-30px) scale(1.1); opacity: 0.85; }
        }
        @keyframes nf-blink { 50% { opacity: 0; } }
        .nf-glow {
          position: absolute;
          width: 700px; height: 700px; border-radius: 50%;
          background: radial-gradient(circle, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 65%);
          filter: blur(60px);
          animation: nf-glow 14s ease-in-out infinite;
          pointer-events: none;
        }
        .nf-num {
          font-family: 'Clash Display', serif;
          font-weight: 600;
          letter-spacing: -0.04em;
          line-height: 0.85;
          position: relative;
          display: inline-block;
        }
        .nf-num::before, .nf-num::after {
          content: attr(data-text);
          position: absolute;
          inset: 0;
          mix-blend-mode: screen;
        }
        .nf-num.glitch::before {
          color: rgba(212,175,55,0.65);
          transform: translate(-3px, 1px);
        }
        .nf-num.glitch::after {
          color: rgba(255,255,255,0.45);
          transform: translate(3px, -1px);
        }
        .nf-cursor {
          display: inline-block; width: 9px; height: 1em;
          background: var(--gold);
          margin-left: 4px; vertical-align: -2px;
          animation: nf-blink 0.9s step-end infinite;
        }
      `}</style>

      <div className="grain" />
      <div className="nf-glow" style={{ top: "-150px", left: "-150px" }} />
      <div
        className="nf-glow"
        style={{
          bottom: "-150px",
          right: "-150px",
          animationDelay: "-7s",
          background:
            "radial-gradient(circle, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 60%)",
        }}
      />

      {/* Top bar */}
      <header
        data-testid="nf-top"
        className="absolute top-0 left-0 right-0 z-20 max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between"
      >
        <a
          href="/"
          data-testid="nf-logo"
          className="font-display text-xl md:text-2xl tracking-tight uppercase"
        >
          Rishikesh<span className="text-gold">.</span>
        </a>
        <a
          href="/"
          data-testid="nf-back-cta"
          className="hidden md:inline-flex items-center gap-2 text-xs font-body uppercase tracking-[0.25em] border border-line text-white px-5 py-2.5 hover:border-gold hover:text-gold transition-colors duration-300"
        >
          Back home <ArrowUpRight size={14} />
        </a>
      </header>

      {/* Main */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-24 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left: massive 404 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="lg:col-span-7"
          data-testid="nf-headline-block"
        >
          <div className="flex items-center gap-3 mb-8 text-xs font-body uppercase tracking-[0.35em] text-[var(--text-muted)]">
            <span className="h-px w-10 bg-gold" />
            Error · Page not found
          </div>

          <h1
            className={`nf-num text-[28vw] md:text-[20vw] lg:text-[16vw] ${
              glitch ? "glitch" : ""
            }`}
            data-text="404"
            data-testid="nf-404"
          >
            404<span className="text-gold">.</span>
          </h1>

          <h2 className="mt-8 font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Plot twist — this page <span className="text-gold">lost the plot.</span>
          </h2>

          <p className="mt-6 max-w-xl text-base md:text-lg text-[var(--text-muted)] font-body leading-relaxed">
            The page you were looking for never made it into print. Maybe it got cut in
            edit. Maybe it was never written. Either way, let's get you back to the
            actual story.
          </p>

          {/* Terminal-ish path */}
          <div
            data-testid="nf-path"
            className="mt-8 inline-flex items-center gap-3 border border-line bg-[var(--bg-elev)] px-4 py-3 font-mono text-xs text-[var(--text-muted)]"
          >
            <span className="text-gold">$</span>
            <span>locate</span>
            <span className="text-white">{typed}</span>
            <span className="nf-cursor" />
          </div>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="/"
              data-testid="nf-cta-home"
              className="group inline-flex items-center gap-3 bg-gold text-black px-7 py-4 text-sm font-body uppercase tracking-[0.2em] hover:bg-white transition-colors duration-300"
            >
              <Home size={16} />
              Take me home
              <ArrowUpRight
                size={16}
                className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
            <a
              href="/#writing"
              data-testid="nf-cta-insights"
              className="group inline-flex items-center gap-3 border border-line text-white px-7 py-4 text-sm font-body uppercase tracking-[0.2em] hover:border-gold hover:text-gold transition-colors duration-300"
            >
              <BookOpen size={16} />
              Read the Insights
            </a>
            <a
              href="mailto:rishikeshn@me.com?subject=Found%20a%20broken%20link"
              data-testid="nf-cta-mail"
              className="group inline-flex items-center gap-3 text-sm font-body uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-gold transition-colors"
            >
              <Mail size={14} />
              Tell me what you were looking for
            </a>
          </div>
        </motion.div>

        {/* Right: editorial filler */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="lg:col-span-5 hidden lg:block"
          data-testid="nf-sidebar"
        >
          <div className="border border-line p-8 bg-[var(--bg-elev)] relative overflow-hidden">
            <div
              className="absolute -inset-10 pointer-events-none opacity-50"
              style={{
                background:
                  "radial-gradient(circle at top right, rgba(212,175,55,0.18), transparent 60%)",
              }}
              aria-hidden
            />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-5 flex items-center gap-3">
                <span className="h-px w-8 bg-gold" />
                Editor's note
              </div>
              <p className="font-display text-2xl md:text-3xl leading-tight text-white mb-6">
                "Communication doesn't fail from a lack of creativity. It fails from a
                lack of <span className="text-gold">clarity.</span>"
              </p>
              <p className="text-sm text-[var(--text-muted)] font-body leading-relaxed mb-8">
                If you can't find what you came for, the link probably wasn't clear.
                Apologies — let me make it up to you. Here's where the real chapters
                live:
              </p>
              <ul className="space-y-3 text-sm">
                {[
                  { label: "About", href: "/#about" },
                  { label: "How I Help", href: "/#services" },
                  { label: "Experience", href: "/#experience" },
                  { label: "Case Studies", href: "/#case-studies" },
                  { label: "Insights", href: "/#writing" },
                  { label: "Contact", href: "/#contact" },
                ].map((l, i) => (
                  <li key={i}>
                    <a
                      href={l.href}
                      data-testid={`nf-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group flex items-center justify-between border-b border-line py-2 hover:border-gold transition-colors"
                    >
                      <span className="text-[var(--text-muted)] group-hover:text-white uppercase tracking-[0.2em] text-xs">
                        {l.label}
                      </span>
                      <ArrowUpRight
                        size={14}
                        className="text-[var(--text-dim)] group-hover:text-gold transition-colors"
                      />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer
        data-testid="nf-footer"
        className="absolute bottom-0 left-0 right-0 z-10 max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-[var(--text-dim)]"
      >
        <div>© {new Date().getFullYear()} Rishikesh N · Bengaluru, IN</div>
        <div>Status code · 404 · Resource not found</div>
      </footer>
    </div>
  );
}
